import {Alert, Button} from '@mui/material';
import React from 'react';

export type BannerSeverity = 'info' | 'success' | 'warning' | 'error';

export type BannerProps = {
  severity: BannerSeverity;
  children: React.ReactNode;
  /** One action, drawn as underlined text in the banner's own ink. */
  action?: {label: string; onClick: () => void; 'data-testid'?: string};
  /**
   * Draws a close button (an "×" in the banner's ink) that calls this. Not
   * with `action`: MUI shows one or the other.
   */
  onClose?: () => void;
  /** The close button's name, translated (MUI's default is English). */
  closeLabel?: string;
};

/**
 * The status tint with its own ink. Info and success are announced politely
 * (role=status); warning and error interrupt (role=alert). MUI puts
 * role=alert on every severity. The icon is decoration: the text carries
 * the meaning.
 */
export const Banner = ({
  severity,
  children,
  action,
  onClose,
  closeLabel,
}: BannerProps) => (
  <Alert
    severity={severity}
    onClose={onClose}
    closeText={closeLabel}
    role={severity === 'info' || severity === 'success' ? 'status' : 'alert'}
    slotProps={{icon: {'aria-hidden': true}}}
    // A phone has no room for the action beside a long message (it squeezed
    // the text into a narrow column): there it drops under the message,
    // aligned with its text (22px icon + 12px gap, less the button's own
    // 5px inset). A close button alone (an icon) stays beside the message.
    sx={(theme) => ({
      [theme.breakpoints.down('sm')]: action && {
        flexWrap: 'wrap',
        '& .MuiAlert-message': {flex: '1 1 0', minWidth: 0},
        '& .MuiAlert-action': {
          flexBasis: '100%',
          ml: '29px',
          mr: 0,
          pl: 0,
          pt: 0,
          pb: 1,
        },
      },
    })}
    action={
      action && (
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={action.onClick}
          data-testid={action['data-testid']}
          sx={{
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            whiteSpace: 'nowrap',
          }}>
          {action.label}
        </Button>
      )
    }>
    {children}
  </Alert>
);
